import { useEffect, useState } from "react";

import {
  listDisconnectionRows,
  listEarthFaultLoopRunsForRevision,
  type DisconnectionRowChoice,
  type EarthFaultLoopRunChoice,
} from "../services/earthing";
import { formatQuantity } from "../utils/formatQuantity";
import { MATERIAL_LABELS } from "./earthingLabels";

// The disconnection-row selector of a circuit protective conductor (EOS-08a, A36, D5): the
// earth-fault loop runs stored under the active open revision, listed from the EOS-05d runs
// API, and the circuit rows of the chosen run from its frozen result. The chosen row's figures
// (the required disconnection time t, the loop current I_f, the protective conductor area S
// and its material) are shown for display only; the backend reads the same frozen row again
// when the study is calculated.

/** The first option: nothing is chosen for the engineer. */
const CHOOSE = "Choose…";

export const NO_EARTH_FAULT_LOOP_RUNS_NOTE = "This revision holds no earth-fault loop run yet.";

export const DISCONNECTION_ROW_HINT =
  "The required disconnection time t, the loop current I_f, the protective conductor area S " +
  "and its material are read from the chosen row of the stored earth-fault loop run when the " +
  "study is calculated; a declared figure below governs over the row's. The check is " +
  "conservative: t is the maximum permitted time, not the device's operating time at I.";

/** "EFL-REF-01 · rev 1 · No solution" */
export function earthFaultLoopRunOptionText(run: EarthFaultLoopRunChoice): string {
  const status = run.design_check_status.replaceAll("_", " ").toLowerCase();
  return `${run.calculation_key} · rev ${run.revision_number} · ${status.charAt(0).toUpperCase()}${status.slice(1)}`;
}

/** "C-01 · Final circuit C-01" */
export function disconnectionRowOptionText(row: DisconnectionRowChoice): string {
  return row.code === row.name ? row.code : `${row.code} · ${row.name}`;
}

/** The read-only line under the selector: the row's figures as the stored result holds them. */
export function linkedRowText(row: DisconnectionRowChoice): string {
  const time = row.required_disconnection_time_s === null ? "t —" : `t ${formatQuantity(row.required_disconnection_time_s, "s").display}`;
  const current = row.fault_current_a === null ? "I_f —" : `I_f ${formatQuantity(row.fault_current_a, "A").display}`;
  const material =
    row.conductor_material === null
      ? ""
      : ` ${((MATERIAL_LABELS as Record<string, string>)[row.conductor_material] ?? row.conductor_material).toLowerCase()}`;
  const area = row.protective_conductor_area_mm2 === null ? "S —" : `S ${formatQuantity(row.protective_conductor_area_mm2, "mm²").display}${material}`;
  const assured =
    row.disconnection_assured === null
      ? "disconnection not compared"
      : row.disconnection_assured
        ? "disconnection assured"
        : "disconnection not assured";
  return `${time} · ${current} · ${area} · ${assured}`;
}

type DisconnectionRowSelectorProps = {
  /** The active open revision (the form shows the no-revision note itself). */
  revisionId: string;
  /** The field name prefix, stable per row ("earth-conductor-3"). */
  name: string;
  /** The run and the row kept in the draft, "" when none. */
  runId: string;
  rowCode: string;
  onChange: (runId: string, rowCode: string) => void;
};

export function DisconnectionRowSelector({ revisionId, name, runId, rowCode, onChange }: DisconnectionRowSelectorProps) {
  const [runs, setRuns] = useState<EarthFaultLoopRunChoice[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [rows, setRows] = useState<DisconnectionRowChoice[] | null>(null);
  const [rowsError, setRowsError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setRuns(null);
    setListError(null);
    listEarthFaultLoopRunsForRevision(revisionId, controller.signal)
      .then((listed) => {
        if (!controller.signal.aborted) {
          setRuns(listed);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setListError(error instanceof Error ? error.message : "The earth-fault loop runs could not be listed.");
        }
      });
    return () => controller.abort();
  }, [revisionId]);

  // A run kept in the draft that the listed revision does not hold is no longer chosen.
  useEffect(() => {
    if (runs !== null && runId !== "" && !runs.some((run) => run.id === runId)) {
      onChange("", "");
    }
  }, [runs, runId, onChange]);

  // The rows of the chosen run, from its stored result.
  useEffect(() => {
    if (runId === "") {
      setRows(null);
      setRowsError(null);
      return;
    }
    const controller = new AbortController();
    setRows(null);
    setRowsError(null);
    listDisconnectionRows(runId, controller.signal)
      .then((listed) => {
        if (!controller.signal.aborted) {
          setRows(listed);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setRowsError(error instanceof Error ? error.message : "The circuit rows could not be read.");
        }
      });
    return () => controller.abort();
  }, [runId]);

  const chosen = rows?.find((row) => row.code === rowCode) ?? null;

  return (
    <>
      <label>
        Earth-fault loop run
        <select name={`${name}-disconnectionRunId`} value={runId} onChange={(event) => onChange(event.target.value, "")}>
          <option value="">{CHOOSE}</option>
          {(runs ?? []).map((run) => (
            <option key={run.id} value={run.id}>
              {earthFaultLoopRunOptionText(run)}
            </option>
          ))}
        </select>
      </label>
      {runs !== null && runs.length === 0 ? (
        <p data-no-earth-fault-loop-runs-note="true">{NO_EARTH_FAULT_LOOP_RUNS_NOTE}</p>
      ) : null}
      {listError !== null ? <p role="alert">{listError}</p> : null}
      {runId !== "" ? (
        <label>
          Circuit row
          <select name={`${name}-disconnectionRowCode`} value={rowCode} onChange={(event) => onChange(runId, event.target.value)}>
            <option value="">{CHOOSE}</option>
            {(rows ?? []).map((row) => (
              <option key={row.code} value={row.code}>
                {disconnectionRowOptionText(row)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {rowsError !== null ? <p role="alert">{rowsError}</p> : null}
      {chosen !== null ? (
        <p data-linked-disconnection-row={chosen.code}>{linkedRowText(chosen)}</p>
      ) : null}
      <p data-disconnection-row-hint="true">{DISCONNECTION_ROW_HINT}</p>
    </>
  );
}

export default DisconnectionRowSelector;
